import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import DuplicateWarning from './DuplicateWarning';
import { 
  STATUS_CONFIG, 
  ROL_CONFIG, 
  STATUS_OPTIONS, 
  ROL_OPTIONS,
  getStatusDescription,
  getRolDescription 
} from '../config/companyFieldsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDialog = ({ open, onOpenChange, company, onSuccess, preselectedType }) => {
  const [formData, setFormData] = useState({
    name: '',
    company_type: 'privada',
    sector: '',
    country: '',
    city: '',
    website: '',
    status: 'Prospecto',
    rol: '',
    owner: ''
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`);
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        company_type: company.company_type || 'privada',
        sector: company.sector || '',
        country: company.country || '',
        city: company.city || '',
        website: company.website || '',
        status: company.status || 'Prospecto',
        rol: company.rol || '',
        owner: company.owner || ''
      });
    } else {
      // Map preselected type to company_type value
      let companyType = 'privada';
      let defaultRol = '';
      
      if (preselectedType === 'Privada') {
        companyType = 'privada';
        defaultRol = ''; // Sin rol por defecto para privadas
      } else if (preselectedType === 'Pública') {
        companyType = 'publica';
        defaultRol = 'Cliente'; // Rol Cliente por defecto para públicas
      }
      
      setFormData({
        name: '',
        company_type: companyType,
        sector: '',
        country: '',
        city: '',
        website: '',
        status: 'Prospecto',
        rol: defaultRol,
        owner: ''
      });
    }
  }, [company, open, preselectedType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre de la empresa es obligatorio');
      return;
    }
    if (!formData.status) {
      toast.error('El estado es obligatorio');
      return;
    }
    if (!formData.rol) {
      toast.error('El rol es obligatorio');
      return;
    }

    setLoading(true);
    try {
      if (company) {
        await axios.put(`${API}/companies/${company.id}`, formData);
        toast.success('Empresa actualizada correctamente');
      } else {
        await axios.post(`${API}/companies`, formData);
        toast.success('Empresa creada correctamente');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Error al guardar la empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-dialog">
        <DialogHeader>
          <DialogTitle>{company ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="company-name-input"
                  required
                />
                <DuplicateWarning
                  entityType="company"
                  query={{ name: formData.name }}
                  excludeId={company?.id}
                />
              </div>
              <div>
                <Label htmlFor="company_type">Tipo de Empresa *</Label>
                <Select
                  value={formData.company_type}
                  onValueChange={(value) => {
                    // Update rol when changing type: Cliente for Pública, empty for Privada
                    const newRol = value === 'publica' ? 'Cliente' : '';
                    setFormData({ ...formData, company_type: value, rol: newRol });
                  }}
                >
                  <SelectTrigger data-testid="company-type-select">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="privada">Privada</SelectItem>
                    <SelectItem value="publica">Pública</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sector">Sector</Label>
                <Input
                  id="sector"
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  data-testid="company-sector-input"
                />
              </div>
              <div>
                <Label htmlFor="country">País</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  data-testid="company-country-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="company-city-input"
                />
              </div>
              <div>
                <Label htmlFor="website">Sitio Web</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  data-testid="company-website-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Estado *</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="company-status-select">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.status && (
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    {getStatusDescription(formData.status)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="rol">Rol *</Label>
                <Select value={formData.rol} onValueChange={(value) => setFormData({ ...formData, rol: value })}>
                  <SelectTrigger data-testid="company-rol-select">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROL_OPTIONS.map((rol) => (
                      <SelectItem key={rol} value={rol}>
                        {rol}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.rol && (
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    {getRolDescription(formData.rol)}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="owner">Responsable</Label>
              <Select
                value={formData.owner || "none"}
                onValueChange={(value) => setFormData({ ...formData, owner: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="company-owner-select">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.email} value={user.name}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-company-btn">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-company-btn">
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDialog;